import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import FieldDate from '@react-form-fields/material-ui/components/Date';
import FormValidation from '@react-form-fields/material-ui/components/FormValidation';
import FieldSelect from '@react-form-fields/material-ui/components/Select';
import FieldText from '@react-form-fields/material-ui/components/Text';
import { theme } from 'assets/theme';
import { FormComponent, IStateForm } from 'components/Abstract/Form';
import ErrorMessage from 'components/Shared/ErrorMessage';
import Snackbar from 'components/Shared/Snackbar';
import { WithStyles } from 'decorators/withStyles';
import { enCriticality, enFrequency, NA } from 'interfaces/document';
import { IFile } from 'interfaces/file';
import React from 'react';
import rxjsOperators from 'rxjs-operators';
import fileService from 'services/file';
import cloneDeep from 'lodash/cloneDeep';

/* eslint-disable max-lines */
interface IState extends IStateForm<IFile> {
  loading: boolean;
  disableFrequency?: boolean;
  error?: null;
}

interface IProps {
  opened: boolean;
  file: IFile;
  onComplete: (file: IFile) => void;
  onCancel: () => void;
  classes?: any;
}

const optionsFrequency = Object.keys(enFrequency).map((f) => ({
  value: f,
  label: enFrequency[f],
}));
const optionsCriticality = Object.keys(enCriticality).map((c) => ({
  value: c,
  label: enCriticality[c],
}));

@WithStyles({
  dialog: {
    width: 700,
    height: 700,
    maxWidth: '100vw',
  },
  root: {
    maxWidth: '100vw',
  },
  Fragment: {
    marginBottom: 20,
    maxWidth: '100vw',
  },
  cardActions: {
    justifyContent: 'flex-end',
  },
  content: {
    width: 1700,
    height: 700,
    maxWidth: '100%',
  },
  textField: {
    margin: 8,
  },
  heading: {
    marginTop: 30,
    marginBottom: 20,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 200,
    maxWidth: '100vw',
  },
  Card: {
    marginTop: 30,
    maxWidth: '100vw',
  },
  Paper: {
    width: 600,
    height: 500,
    maxWidth: '100vw',
  },
  dialogActions: {
    padding: theme.spacing(2),
  },
})
export default class FileFormDialog extends FormComponent<IProps, IState> {
  constructor(props: {}) {
    super(props);
    this.state = {
      ...this.state,
      disableFrequency: false,
      loading: true,
    };
  }

  handleEnter = () => {
    this.setState({
      model: cloneDeep(this.props.file),
      loading: false,
      disableFrequency: [
        this.state.model.frequency,
        this.props.file.frequency,
      ].some((item) => item === NA),
    });
  };

  handleExit = () => {
    this.resetForm();
  };

  tryAgain = () => {
    this.setState({ loading: false, error: null });
  };

  onSubmit = (isValid: boolean) => {
    const { model } = this.state;

    if (!isValid) return;

    this.setState({ loading: true });

    fileService
      .edit(model as IFile)
      .pipe(rxjsOperators.logError(), rxjsOperators.bindComponent(this))
      .subscribe(
        (file) => {
          Snackbar.show(`${file.filename} editado com sucesso!`);
          this.setState({ loading: false });

          this.props.onComplete(file);
        },
        (err) => {
          Snackbar.error(err.message);
          this.setState({ loading: false });
        }
      );
  };

  handleFrequencyNA = () => {
    const disableFrequency = !this.state.disableFrequency;
    const frequency = disableFrequency ? NA : '';
    const expireDate = new Date(2099, 11, 31, 0, 0, 0, 0);
    this.setState({ disableFrequency });
    this.updateModel((model, v) => {
      if (frequency === NA) {
        model.expirationDate = expireDate;
      }
      model.frequency = v;
    })(frequency);
  };

  render() {
    const { model, loading, error, disableFrequency } = this.state;
    const { opened, classes, onCancel, file } = this.props;

    return (
      <Dialog
        open={opened}
        disableBackdropClick
        disableEscapeKeyDown
        onEnter={this.handleEnter}
        onExited={this.handleExit}
      >
        {loading && <LinearProgress color="secondary" />}

        <FormValidation onSubmit={this.onSubmit} ref={this.formValidationRef}>
          <DialogTitle>Editar Arquivo</DialogTitle>
          <DialogContent className={classes.content}>
            {error && <ErrorMessage error={error} tryAgain={this.tryAgain} />}

            {!error && (
              <Typography component="div" dir={theme.direction}>
                <div className={classes.field}>
                  <FieldText
                    label="Arquivo"
                    validation="required|string|max:100"
                    disabled={loading}
                    value={model.filename}
                    onChange={this.updateModel(
                      (model, v) => (model.filename = v)
                    )}
                  />
                  <FieldDate
                    label="Data de emissão"
                    className={classes.FieldText}
                    disabled={loading}
                    value={model.emissionDate}
                    onChange={this.updateModel(
                      (model, v) => (model.emissionDate = v)
                    )}
                  />
                  <FieldDate
                    label="Data de vencimento"
                    className={classes.FieldText}
                    disabled={loading || model.frequency === NA}
                    helperText={
                      file.frequency === NA
                        ? 'Documentos cuja periodicidade não se aplique (N/A) não possuem data de vencimento'
                        : ''
                    }
                    value={
                      model.expirationDate
                        ? model.expirationDate
                        : new Date('2099-12-31T00:00:00')
                    }
                    onChange={this.updateModel(
                      (model, v) => (model.expirationDate = v)
                    )}
                  />
                  <FieldSelect
                    label="Periodicidade"
                    disabled={loading || disableFrequency}
                    value={model.frequency}
                    options={
                      disableFrequency
                        ? [{ value: NA, label: NA }]
                        : optionsFrequency
                    }
                    validation="required"
                    onChange={this.updateModel(
                      (model, v) => (model.frequency = v)
                    )}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={disableFrequency}
                        onChange={this.handleFrequencyNA}
                        value="disableFrequency"
                        color="primary"
                      />
                    }
                    label="N/A - Não Aplicável"
                  />
                  <FieldSelect
                    label="Criticalidade"
                    name="criticality"
                    value={model.criticality}
                    options={optionsCriticality}
                    onChange={this.updateModel(
                      (model, v) => (model.criticality = v)
                    )}
                  />
                </div>
              </Typography>
            )}
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button onClick={onCancel}>Cancelar</Button>
            <Button
              color="primary"
              variant={'contained'}
              type="submit"
              disabled={loading || !!error}
            >
              Salvar
            </Button>
          </DialogActions>
        </FormValidation>
      </Dialog>
    );
  }
}
